import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { SiteShell } from "@/components/SiteShell";
import { useLocale } from "@/contexts/LocaleContext";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import {
  Loader2,
  PackageCheck,
  AlertCircle,
  RotateCcw,
  MessageCircle,
  ShoppingBag,
} from "lucide-react";
import { LECMO_WA_NUMBER } from "@/lib/agentConfig";

// ─────────────────────────────────────────────────────────────────────────────
// إيصال الطلب — /order/<token>
// صفحة خاصّة بالطلب نفسه، تُفتح من زرّ «إيصال الطلب» في رسائل التسليم/الاسترجاع.
// التوكن الموقَّع هو الإثبات (نفس توكن /track-order) — بلا تسجيل دخول.
// ─────────────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/order/$token")({
  component: OrderReceiptPage,
  head: () => ({
    meta: [
      { title: "Order receipt — Lecmo" },
      {
        name: "description",
        content: "Your Lecmo order receipt: items, total, and delivery confirmation.",
      },
      { property: "og:title", content: "Order receipt — Lecmo" },
      {
        property: "og:description",
        content: "View the receipt of your Lecmo order, including items and delivery status.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      // صفحة خاصّة بطلب واحد — لا تُفهرَس
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/track-order` }],
  }),
});

interface ReceiptResult {
  orderName: string;
  createdAt: string;
  financialStatus: string | null;
  cancelledAt?: string | null;
  fulfillmentStatus: string;
  items: Array<{ title: string; qty: number; image?: string | null }>;
  localDelivery?: {
    status: string;
    deliveredAt: string | null;
  } | null;
}

function OrderReceiptPage() {
  const { token } = Route.useParams();
  const { locale, dir } = useLocale();
  const t = (en: string, ar: string) => (locale === "ar" ? ar : en);

  const [loading, setLoading] = React.useState(true);
  const [result, setResult] = React.useState<ReceiptResult | null>(null);
  const [errorCode, setErrorCode] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorCode(null);
    setResult(null);
    fetch("/api/track-order", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ t: token }),
    })
      .then(async (res) => {
        const data = (await res.json().catch(() => null)) as
          | (ReceiptResult & { error?: string })
          | null;
        if (cancelled) return;
        if (!res.ok || !data || data.error) {
          setErrorCode(data?.error ?? "service_unavailable");
          return;
        }
        setResult(data);
      })
      .catch(() => {
        if (!cancelled) setErrorCode("service_unavailable");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const statusLabel = (r: ReceiptResult): { label: string; hint: string } => {
    const fin = (r.financialStatus ?? "").toLowerCase();
    if (r.cancelledAt) {
      return {
        label: t("Cancelled", "أُلغي الطلب"),
        hint: t("This order was cancelled.", "هذا الطلب أُلغي."),
      };
    }
    if (fin === "refunded") {
      return {
        label: t("Refunded", "تمّ الاسترجاع"),
        hint: t(
          "The amount was refunded to your original payment method.",
          "أُعيد المبلغ إلى وسيلة الدفع نفسها.",
        ),
      };
    }
    if (r.fulfillmentStatus === "delivered" || r.localDelivery?.status === "delivered") {
      return {
        label: t("Delivered", "تمّ التسليم"),
        hint: t("We hope you love it.", "نتمنّى أن يسرّك."),
      };
    }
    if (r.fulfillmentStatus === "fulfilled" || r.fulfillmentStatus === "partial") {
      return {
        label: t("Shipped", "تمّ الشحن"),
        hint: t("Your order is on the way.", "طلبك في الطريق إليك."),
      };
    }
    return {
      label: t("Being prepared", "قيد التجهيز"),
      hint: t("We are packing your order.", "نجهّز طلبك الآن."),
    };
  };

  const deliveredAt = result?.localDelivery?.deliveredAt ?? null;

  const dateFmt = (iso: string) =>
    new Date(iso).toLocaleDateString(locale === "ar" ? "ar-AE" : "en-AE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <SiteShell>
      <div dir={dir} className="mx-auto w-full max-w-2xl px-4 py-10 md:py-14">
        <h1 className="font-serif text-2xl md:text-3xl text-foreground">
          {t("Order receipt", "إيصال الطلب")}
        </h1>

        {loading && (
          <div className="mt-10 flex items-center gap-3 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">{t("Loading your order…", "جاري تحميل طلبك…")}</span>
          </div>
        )}

        {!loading && errorCode && (
          <div className="mt-8 rounded-lg border border-border bg-card p-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 text-destructive" />
              <div className="space-y-2">
                <p className="text-sm text-foreground">
                  {errorCode === "invalid_token" || errorCode === "invalid_input"
                    ? t(
                        "This receipt link is no longer valid or has expired.",
                        "هذا الرابط غير صالح أو انتهت صلاحيّته.",
                      )
                    : errorCode === "not_found"
                      ? t("We could not find this order.", "لم نجد هذا الطلب.")
                      : t(
                          "We could not load your order right now. Please try again shortly.",
                          "تعذّر تحميل الطلب الآن. حاول بعد قليل.",
                        )}
                </p>
                <Link
                  to="/track-order"
                  className="inline-flex min-h-[40px] items-center gap-2 text-sm text-primary underline underline-offset-4"
                >
                  <RotateCcw className="h-4 w-4" />
                  {t("Look up your order manually", "ابحث عن طلبك يدويّاً")}
                </Link>
              </div>
            </div>
          </div>
        )}

        {!loading && result && (
          <div className="mt-8 space-y-6">
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    {t("Order", "الطلب")}
                  </p>
                  <p className="mt-1 font-serif text-xl text-foreground">{result.orderName}</p>
                </div>
                <div className="text-end">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    {t("Date", "التاريخ")}
                  </p>
                  <p className="mt-1 text-sm text-foreground">{dateFmt(result.createdAt)}</p>
                </div>
              </div>

              <div className="mt-5 flex items-start gap-3 border-t border-border pt-4">
                <PackageCheck className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">{statusLabel(result).label}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{statusLabel(result).hint}</p>
                  {deliveredAt && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("Delivered on", "تاريخ التسليم")}: {dateFmt(deliveredAt)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {t("Items", "المنتجات")}
              </p>
              <ul className="mt-4 space-y-4">
                {result.items.map((item, idx) => (
                  <li key={`${item.title}-${idx}`} className="flex items-center gap-4">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className="h-16 w-16 shrink-0 rounded-md object-cover"
                      />
                    ) : (
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-muted">
                        <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-sm text-foreground">{item.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t("Quantity", "الكميّة")}: {item.qty}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/track-order"
                search={{ t: token }}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-border px-4 text-sm text-foreground"
              >
                <RotateCcw className="h-4 w-4" />
                {t("Live status", "الحالة اللحظيّة")}
              </Link>
              <a
                href={`https://wa.me/${LECMO_WA_NUMBER}?text=${encodeURIComponent(
                  `${result.orderName}`,
                )}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-emerald-600 px-4 text-sm text-white"
              >
                <MessageCircle className="h-4 w-4" />
                {t("Need help?", "تحتاج مساعدة؟")}
              </a>
            </div>
          </div>
        )}
      </div>
    </SiteShell>
  );
}
